import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { ApolloQueryResult, useLazyQuery, useMutation } from '@apollo/client';
import { Pagination } from 'apolloClient/types/common';
import useSnackbar from 'components/SnackBar/useSnackbar';
import { FavoriteBuildingsResponse } from 'apolloClient/types/FavoriteBuilding';
import { GET_FAVORITE_BUILDINGS } from 'apolloClient/queries/favoriteBuildings';
import {
  CREATE_FAVORITE_BUILDING,
  DELETE_FAVORITE_BUILDING,
} from 'apolloClient/mutations/favoriteBuildings';
import { useAuth } from 'components/Auth/AuthProvider';

export type FavoritesBuildingsVariables = {
  building: number;
  alertFrequency: string;
};

type Response = { favoriteBuildings: FavoriteBuildingsResponse };

type FetchResult = ApolloQueryResult<Response>;

export type LatesAction = {
  buildingId: number;
  favoriteId: number;
  action: 'delete' | 'create';
} | null;

export type FavoritesBuildingsContextObject = {
  favorites: FavoriteBuildingsResponse;
  deleteFavorite: (params: { id: number; buildingId: number }) => Promise<void>;
  createFavorite: (data: FavoritesBuildingsVariables) => Promise<boolean>;
  getFavorites: (page?: number) => void;
  loading?: boolean;
  pagination: Pagination;
  latestAction: LatesAction;
  setLatestAction: (action: LatesAction) => void;
  count: number;
};

const NOOP = () => new Promise<Partial<FetchResult>>((res) => res({}));

export const FavoritesBuildingsContext =
  createContext<FavoritesBuildingsContextObject>({
    favorites: { data: [] },
    deleteFavorite: () => new Promise<void>((res) => res()),
    createFavorite: () => Promise.resolve(false),
    getFavorites: NOOP,
    pagination: {
      total: 0,
    },
    latestAction: null,
    setLatestAction: () => {},
    count: 0,
  });

export const useFavoriteBuilding = () => useContext(FavoritesBuildingsContext);

const paginationPageSize = 6;

const FavoritesBuildingsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isLogged, favoritesBuildingsCount, getActualAuthData } = useAuth();

  const { showSuccess, showError } = useSnackbar();
  const [fetch, { data, updateQuery, loading }] = useLazyQuery<
    Response,
    { page: number; pageSize: number; userId: number }
  >(GET_FAVORITE_BUILDINGS, {
    variables: { page: 1, pageSize: paginationPageSize, userId: 0 },
    fetchPolicy: 'no-cache',
  });
  const [createFavoriteMutation] = useMutation<
    { createFavoriteBuilding: { data: { id: number } } },
    { data: FavoritesBuildingsVariables }
  >(CREATE_FAVORITE_BUILDING);
  const [deleteFavoriteMutation] = useMutation<
    { deleteFavoriteBuilding: null },
    { id?: number }
  >(DELETE_FAVORITE_BUILDING);

  const [latestAction, setLatestAction] = useState<LatesAction>(null);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (favoritesBuildingsCount) {
      setCount(
        count >= favoritesBuildingsCount
          ? count
          : favoritesBuildingsCount + count
      );
    }
  }, [favoritesBuildingsCount]);

  useEffect(() => {
    if (!isLogged && data) {
      updateQuery(() => ({
        favoriteBuildings: {
          data: [],
          meta: {
            pagination: {
              page: 1,
              pageCount: 0,
              pageSize: 0,
              total: 0,
            },
          },
        },
      }));
    }
  }, [isLogged, updateQuery, data]);

  const getFavorites = useCallback(
    (page?: number) => {
      const auth = getActualAuthData();

      if (!auth.user.id) return;
      fetch({
        variables: {
          page: page || 1,
          pageSize: paginationPageSize,
          userId: auth.user.id,
        },
      });
    },
    [fetch, getActualAuthData]
  );

  const createFavorite = async (data: FavoritesBuildingsVariables) => {
    try {
      const { data: resp } = await createFavoriteMutation({
        variables: {
          data,
        },
      });
      const favoriteId = resp?.createFavoriteBuilding?.data?.id;

      if (!favoriteId) return false;

      setLatestAction({
        action: 'create',
        favoriteId,
        buildingId: data.building,
      });
      showSuccess({ message: 'Saved to favorites successfully' });
      setCount((prevCount) => prevCount + 1);
      return true;
    } catch (err) {
      if (err instanceof Error && err.message === 'Already exists') {
        showError({ message: 'Building already saved' });
      }
      return false;
    }
  };

  const deleteFavorite = async ({
    id,
    buildingId,
  }: {
    id: number;
    buildingId: number;
  }) => {
    const res = await deleteFavoriteMutation({ variables: { id } }).then(
      (resp) => {
        if (resp?.data) {
          setLatestAction({
            action: 'delete',
            buildingId,
            favoriteId: id,
          });
          showSuccess({ message: 'Deleted from favorites successfully' });
          setCount((prevCount) => prevCount - 1);
        }
      }
    );
  };

  const pagination = data?.favoriteBuildings?.meta?.pagination || {};

  return (
    <FavoritesBuildingsContext.Provider
      value={{
        favorites: data?.favoriteBuildings || { data: [] },
        createFavorite,
        deleteFavorite,
        getFavorites,
        loading,
        pagination,
        latestAction,
        setLatestAction,
        count,
      }}
    >
      {children}
    </FavoritesBuildingsContext.Provider>
  );
};

export default FavoritesBuildingsProvider;
